/**
 * Edit Assignment Modal
 * Modal for editing assignment details (course, students, due date, time limit, attempts allowed)
 */

import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiCalendar, FiClock } from 'react-icons/fi';
import quizApiService from '../../services/quizApi';

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--background-overlay);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled(motion.div)`
  background: var(--background-primary);
  border-radius: 0;
  box-shadow: var(--shadow-lg);
  border: 1px solid var(--border-color);
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  padding: 1.5rem 1.5rem 1rem;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  color: var(--text-primary);
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0;
  transition: var(--transition);

  &:hover {
    background: var(--background-tertiary);
    color: var(--text-primary);
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
`;


const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  font-size: 1rem;
  background: var(--background-primary);
  color: var(--text-primary);
  transition: var(--transition);

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--focus-ring);
  }
`;

const TimePickerContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const TimePickerGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const CustomInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  font-size: 1rem;
  background: var(--background-primary);
  color: var(--text-primary);
  transition: var(--transition);

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--focus-ring);
  }
`;

const DateTimeInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 0;
  font-size: 1rem;
  background: var(--background-primary);
  color: var(--text-primary);
  transition: var(--transition);

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--focus-ring);
  }

  &::-webkit-calendar-picker-indicator {
    background: transparent;
    bottom: 0;
    color: transparent;
    cursor: pointer;
    height: auto;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
    width: auto;
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2364748b'%3e%3cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z'/%3e%3c/svg%3e");
    background-size: 20px 20px;
    background-repeat: no-repeat;
    background-position: right 12px center;
    padding-right: 40px;
  }

  &:hover {
    border-color: var(--border-dark);
    background: var(--background-primary);
    transform: translateY(-1px);
    box-shadow: var(--shadow-sm);
  }

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    background: var(--background-primary);
    box-shadow: 0 0 0 3px var(--focus-ring);
    transform: translateY(-2px);
  }

  &::placeholder {
    color: var(--text-muted);
    font-style: italic;
  }

  /* Firefox */
  &::-moz-calendar-picker-indicator {
    background: transparent;
    color: transparent;
    cursor: pointer;
    height: 20px;
    width: 20px;
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2364748b'%3e%3cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z'/%3e%3c/svg%3e");
    background-size: 20px 20px;
    background-repeat: no-repeat;
    background-position: center;
    margin-right: 8px;
  }
`;

const CustomOptionContainer = styled.div`
  margin-top: 0.5rem;
`;

const CustomOptionGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;


const StudentList = styled.div`
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid var(--border-color);
  border-radius: 0;
  background: var(--background-primary);
`;

const StudentItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border-bottom: 1px solid var(--border-color);
  transition: var(--transition);

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: var(--background-secondary);
  }
`;

const StudentInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const StudentName = styled.span`
  font-weight: 500;
  color: var(--text-primary);
  font-size: 0.875rem;
`;

const StudentEmail = styled.span`
  color: var(--text-secondary);
  font-size: 0.75rem;
  margin-top: 0.125rem;
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  accent-color: var(--primary-color);
`;

const SelectAllButton = styled.button`
  background: var(--background-secondary);
  color: var(--text-secondary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  cursor: pointer;
  transition: var(--transition);
  margin-bottom: 1rem;

  &:hover {
    background: var(--background-tertiary);
    color: var(--text-primary);
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 2rem;
  color: var(--text-secondary);
`;


const ModalFooter = styled.div`
  padding: 1rem 1.5rem 1.5rem;
  border-top: 1px solid var(--border-color);
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: 1px solid transparent;
  border-radius: 0;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: var(--transition);
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &.secondary {
    background: var(--background-secondary);
    color: var(--text-secondary);
    border-color: var(--border-color);

    &:hover {
      background: var(--background-tertiary);
      color: var(--text-primary);
    }
  }

  &.primary {
    background: var(--primary-color);
    color: var(--text-inverse);
    border-color: var(--primary-color);

    &:hover {
      background: var(--primary-hover);
      border-color: var(--primary-hover);
    }

    &:disabled {
      background: var(--text-muted);
      border-color: var(--text-muted);
      cursor: not-allowed;
    }
  }
`;

const ErrorMessage = styled.div`
  color: var(--danger-color);
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

function EditAssignmentModal({ isOpen, onClose, assignment, onSave }) {
  const [formData, setFormData] = useState({
    course_id: '',
    due_at: '',
    time_limit_minutes: null,
    attempts_allowed: 1,
    immediate_feedback: true
  });
  const [customTimeLimit, setCustomTimeLimit] = useState('');
  const [useCustomTimeLimit, setUseCustomTimeLimit] = useState(false);
  const [customAttempts, setCustomAttempts] = useState('');
  const [useCustomAttempts, setUseCustomAttempts] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [courses, setCourses] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);

  // Fetch assignment details and related data
  const fetchAssignmentData = useCallback(async () => {
    if (!assignment || !isOpen) return;

    try {
      setDataLoading(true);
      setError(null);
      console.log('Fetching assignment data for:', assignment);

      const [assignmentResponse, coursesResponse] = await Promise.all([
        quizApiService.getAssignmentDetails(assignment.assignment_id),
        quizApiService.getCourses()
      ]);

      if (!assignmentResponse.success) {
        console.error('Assignment response error:', assignmentResponse);
        setError('Failed to fetch assignment details');
        return;
      }

      if (!coursesResponse.success) {
        setError('Failed to fetch courses');
        return;
      }

      const assignmentData = assignmentResponse.data;
      const coursesData = coursesResponse.courses || coursesResponse.data;

      // Format due date for separate date and time inputs
      // FIX: Properly format date and time for date/time inputs
      let dueDate = '';
      if (assignmentData.due_at) {
        const date = new Date(assignmentData.due_at);
        // Format as YYYY-MM-DDTHH:mm for date/time inputs (local time, not UTC)
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        dueDate = `${year}-${month}-${day}T${hours}:${minutes}`;
      }
      
      setFormData({
        course_id: assignmentData.course_id || '',
        due_at: dueDate,
        time_limit_minutes: assignmentData.time_limit_minutes,
        attempts_allowed: assignmentData.attempts_allowed || 1,
        immediate_feedback: assignmentData.immediate_feedback !== undefined ? assignmentData.immediate_feedback : true
      });

      setCourses(coursesData);
      setStudents(assignmentData.assigned_students || []);
      setSelectedStudents(assignmentData.assigned_students?.map(s => s.id) || []);

      // Check if time limit is custom (not in predefined options)
      const predefinedLimits = [15, 30, 45, 60, 90, 120];
      if (assignmentData.time_limit_minutes && !predefinedLimits.includes(assignmentData.time_limit_minutes)) {
        setCustomTimeLimit(assignmentData.time_limit_minutes.toString());
        setUseCustomTimeLimit(true);
      } else {
        setCustomTimeLimit('');
        setUseCustomTimeLimit(false);
      }

      // Check if attempts is custom (not in predefined options)
      const predefinedAttempts = [1, 2, 3, 5, 10, 999];
      if (assignmentData.attempts_allowed && !predefinedAttempts.includes(assignmentData.attempts_allowed)) {
        setCustomAttempts(assignmentData.attempts_allowed.toString());
        setUseCustomAttempts(true);
      } else {
        setCustomAttempts('');
        setUseCustomAttempts(false);
      }

    } catch (err) {
      console.error('Error fetching assignment data:', err);
      setError('Failed to fetch assignment data');
    } finally {
      setDataLoading(false);
    }
  }, [assignment, isOpen]);

  useEffect(() => {
    fetchAssignmentData();
  }, [fetchAssignmentData]);

  // Fetch students when course changes
  const fetchStudentsForCourse = useCallback(async (courseId) => {
    if (!courseId) {
      setStudents([]);
      setSelectedStudents([]);
      return;
    }

    try {
      const studentsResponse = await quizApiService.getStudentsByCourse(courseId);
      
      if (studentsResponse.success) {
        setStudents(studentsResponse.data);
        setSelectedStudents([]); // Reset selected students when course changes
      } else {
        setError('Failed to fetch students for this course');
        setStudents([]);
      }
    } catch (err) {
      console.error('Error fetching students:', err);
      setError('Failed to fetch students for this course');
      setStudents([]);
    }
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
    
    if (field === 'course_id') {
      fetchStudentsForCourse(value);
    }
  };

  const handleTimeLimitSelectChange = (value) => {
    if (value === 'custom') {
      setUseCustomTimeLimit(true);
    } else {
      setUseCustomTimeLimit(false);
      setCustomTimeLimit('');
      setFormData(prev => ({ ...prev, time_limit_minutes: value ? parseInt(value) : null }));
    }
  };

  const handleCustomTimeLimitChange = (value) => {
    setCustomTimeLimit(value);
    if (value && !isNaN(value) && value > 0) {
      setFormData(prev => ({ ...prev, time_limit_minutes: parseInt(value) }));
    }
  };

  const handleCustomAttemptsChange = (value) => {
    setCustomAttempts(value);
    if (value && !isNaN(value) && value > 0) {
      setFormData(prev => ({ ...prev, attempts_allowed: parseInt(value) }));
    }
  };

  const handleAttemptsSelectChange = (value) => {
    if (value === 'custom') {
      setUseCustomAttempts(true);
    } else {
      setUseCustomAttempts(false);
      setCustomAttempts('');
      setFormData(prev => ({ ...prev, attempts_allowed: parseInt(value) }));
    }
  };

  const handleStudentToggle = (studentId) => {
    setSelectedStudents(prev => {
      if (prev.includes(studentId)) {
        return prev.filter(id => id !== studentId);
      } else {
        return [...prev, studentId];
      }
    });
  };

  const handleSelectAllStudents = () => {
    if (selectedStudents.length === students.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(students.map(s => s.id));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (!formData.course_id) {
        setError('Please select a course');
        return;
      }

      if (selectedStudents.length === 0) {
        setError('Please select at least one student');
        return;
      }

      // Convert datetime-local to ISO string for API
      const dueAt = formData.due_at ? new Date(formData.due_at).toISOString() : null;
      
      const updateData = {
        course_id: formData.course_id,
        studentIds: selectedStudents,
        due_at: dueAt,
        time_limit_minutes: formData.time_limit_minutes,
        attempts_allowed: formData.attempts_allowed,
        immediate_feedback: formData.immediate_feedback
      };

      await onSave(assignment.assignment_id, updateData);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to update assignment');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !assignment) return null;

  return (
    <AnimatePresence>
      <ModalOverlay
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <ModalContent
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          <ModalHeader>
            <ModalTitle>Edit Assignment</ModalTitle>
            <CloseButton onClick={onClose}>
              <FiX size={20} />
            </CloseButton>
          </ModalHeader>

          <form onSubmit={handleSubmit}>
            <ModalBody>
              {dataLoading ? (
                <LoadingState>Loading assignment details...</LoadingState>
              ) : (
                <>
                  <FormGroup>
                    <Label htmlFor="course_id">Select Course *</Label>
                    <Select
                      id="course_id"
                      value={formData.course_id}
                      onChange={(e) => handleInputChange('course_id', e.target.value)}
                    >
                      <option value="">Select a course</option>
                      {courses && courses.map(course => (
                        <option key={course.id} value={course.id}>
                          {course.code} - {course.name}
                        </option>
                      ))}
                    </Select>
                  </FormGroup>

                  <FormGroup>
                    <Label>
                      Select Students {formData.course_id && courses ? `from ${courses.find(c => c.id === formData.course_id)?.name || 'selected course'}` : ''} *
                    </Label>
                    {students.length > 0 && (
                      <SelectAllButton type="button" onClick={handleSelectAllStudents}>
                        {selectedStudents.length === students.length ? 'Deselect All' : 'Select All'}
                      </SelectAllButton>
                    )}
                    <StudentList>
                      {students.length === 0 ? (
                        <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
                          {formData.course_id ? 'No students found in this course' : 'Select a course to see students'}
                        </div>
                      ) : (
                        students.map(student => (
                          <StudentItem key={student.id}>
                            <Checkbox
                              type="checkbox"
                              checked={selectedStudents.includes(student.id)}
                              onChange={() => handleStudentToggle(student.id)}
                            />
                            <StudentInfo>
                              <StudentName>{student.name}</StudentName>
                              <StudentEmail>{student.email}</StudentEmail>
                            </StudentInfo>
                          </StudentItem>
                        ))
                      )}
                    </StudentList>
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="due_at">
                      <FiCalendar size={16} style={{ marginRight: '0.5rem' }} />
                      Due Date/Time (Optional)
                    </Label>
                    <TimePickerContainer>
                      <TimePickerGroup>
                        <Label htmlFor="due_date" style={{ fontSize: '0.75rem', marginBottom: '0.5rem' }}>Date</Label>
                        <DateTimeInput
                          id="due_date"
                          type="date"
                          value={formData.due_at ? formData.due_at.split('T')[0] : ''}
                          onChange={(e) => {
                            const date = e.target.value;
                            const time = formData.due_at ? formData.due_at.split('T')[1] || '00:00' : '00:00';
                            handleInputChange('due_at', date ? `${date}T${time}` : '');
                          }}
                        />
                      </TimePickerGroup>
                      <TimePickerGroup>
                        <Label htmlFor="due_time" style={{ fontSize: '0.75rem', marginBottom: '0.5rem' }}>Time</Label>
                        <DateTimeInput
                          id="due_time"
                          type="time"
                          value={formData.due_at ? (formData.due_at.split('T')[1] || '00:00').substring(0, 5) : '00:00'}
                          onChange={(e) => {
                            const time = e.target.value || '00:00';
                            const date = formData.due_at ? formData.due_at.split('T')[0] : '';
                            handleInputChange('due_at', date ? `${date}T${time}` : '');
                          }}
                        />
                      </TimePickerGroup>
                    </TimePickerContainer>
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="time_limit_minutes">
                      <FiClock size={16} style={{ marginRight: '0.5rem' }} />
                      Time Limit (Optional)
                    </Label>
                    <Select
                      id="time_limit_minutes"
                      value={useCustomTimeLimit ? 'custom' : (formData.time_limit_minutes ? formData.time_limit_minutes.toString() : '')}
                      onChange={(e) => handleTimeLimitSelectChange(e.target.value)}
                    >
                      <option value="">No time limit</option>
                      <option value="15">15 minutes</option>
                      <option value="30">30 minutes</option>
                      <option value="45">45 minutes</option>
                      <option value="60">1 hour</option>
                      <option value="90">1.5 hours</option>
                      <option value="120">2 hours</option>
                      <option value="custom">Other (specify below)</option>
                    </Select>
                    
                    {useCustomTimeLimit && (
                      <CustomOptionContainer>
                        <CustomOptionGroup>
                          <CustomInput
                            type="number"
                            placeholder="Enter minutes"
                            value={customTimeLimit}
                            onChange={(e) => handleCustomTimeLimitChange(e.target.value)}
                            min="1"
                          />
                          <span>minutes</span>
                        </CustomOptionGroup>
                      </CustomOptionContainer>
                    )}
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="attempts_allowed">Attempts Allowed</Label>
                    <Select
                      id="attempts_allowed"
                      value={useCustomAttempts ? 'custom' : (formData.attempts_allowed ? formData.attempts_allowed.toString() : '1')}
                      onChange={(e) => handleAttemptsSelectChange(e.target.value)}
                    >
                      <option value="1">1 attempt</option>
                      <option value="2">2 attempts</option>
                      <option value="3">3 attempts</option>
                      <option value="5">5 attempts</option>
                      <option value="10">10 attempts</option>
                      <option value="999">Unlimited</option>
                      <option value="custom">Other (specify below)</option>
                    </Select>
                    
                    {useCustomAttempts && (
                      <CustomOptionContainer>
                        <CustomOptionGroup>
                          <CustomInput
                            type="number"
                            placeholder="Enter number of attempts"
                            value={customAttempts}
                            onChange={(e) => handleCustomAttemptsChange(e.target.value)}
                            min="1"
                          />
                          <span>attempts</span>
                        </CustomOptionGroup>
                      </CustomOptionContainer>
                    )}
                  </FormGroup>

                  <FormGroup>
                    <CheckboxGroup>
                      <Checkbox
                        id="immediate_feedback"
                        type="checkbox"
                        checked={formData.immediate_feedback}
                        onChange={(e) => handleInputChange('immediate_feedback', e.target.checked)}
                      />
                      <Label htmlFor="immediate_feedback">Show immediate feedback after submission</Label>
                    </CheckboxGroup>
                  </FormGroup>

                  {error && <ErrorMessage>{error}</ErrorMessage>}
                </>
              )}
            </ModalBody>

            <ModalFooter>
              <Button type="button" className="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" className="primary" disabled={loading}>
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </ModalOverlay>
    </AnimatePresence>
  );
}

export default EditAssignmentModal;
